import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import StudentForm from './StudentForm';
import Notification from '../common/Notification';
import '../../styles/Students.css';
import { fetchStudents, addStudent, updateStudent } from '../../utils/supabaseClient';

// Format column name for display (moved outside component to avoid initialization issues)
const formatColumnName = (columnName) => {
  if (!columnName) return '';
  // Convert camelCase to Title Case with spaces
  return columnName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

function Students() {  
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('Rno');
  const [sortDirection, setSortDirection] = useState('asc');
  const [editingStudent, setEditingStudent] = useState(null);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(50);
  const [columns, setColumns] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [currentStudentAction, setCurrentStudentAction] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [activeFilter, setActiveFilter] = useState('active');
  const [showActiveDropdown, setShowActiveDropdown] = useState(false);
  const [filters, setFilters] = useState([]);
  const [showFilterBuilder, setShowFilterBuilder] = useState(false);
  const [showInlineStatusConfirm, setShowInlineStatusConfirm] = useState(false);

  const FILTER_OPERATORS = [
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
    { value: 'greater_than', label: 'greater than' },
    { value: 'less_than', label: 'less than' },
  ];

  const addFilter = (connector = 'AND') => {
    setFilters(prev => [...prev, { connector: prev.length === 0 ? 'AND' : connector, column: columns[0] || 'Rno', operator: 'contains', value: '' }]);
    setShowFilterBuilder(true);
  };
  const updateFilter = (index, field, value) => {
    setFilters(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  };
  const removeFilter = (index) => {
    setFilters(prev => prev.filter((_, i) => i !== index));
  };
  const clearAllFilters = () => {
    setFilters([]);
  };

  const applyFilter = (student, filter) => {
    const raw = student[filter.column];
    const cellVal = String(raw ?? '').toLowerCase();
    const filterVal = (filter.value || '').toLowerCase();
    switch (filter.operator) {
      case 'contains': return cellVal.includes(filterVal);
      case 'not_contains': return !cellVal.includes(filterVal);
      case 'equals': return cellVal === filterVal;
      case 'not_equals': return cellVal !== filterVal;
      case 'starts_with': return cellVal.startsWith(filterVal);
      case 'ends_with': return cellVal.endsWith(filterVal);
      case 'is_empty': return raw === null || raw === undefined || String(raw).trim() === '';
      case 'is_not_empty': return raw !== null && raw !== undefined && String(raw).trim() !== '';
      case 'greater_than': return parseFloat(raw) > parseFloat(filter.value);
      case 'less_than': return parseFloat(raw) < parseFloat(filter.value);
      default: return true;
    }
  };

  // Cell selection & inline edit
  const [selectedCell, setSelectedCell] = useState(null); // { rno, column } | null
  const [editingCell, setEditingCell] = useState(null); // { rno, column } | null
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(false);
  const tableRef = useRef(null);
  const editRnoHandled = useRef(false);

  // Columns that cannot be inline-edited (primary key, computed, etc.)
  const NON_EDITABLE_COLUMNS = ['Rno'];

  const isDateColumn = (col) => col.toLowerCase().includes('date') || col.toLowerCase() === 'dob';

  const startInlineEdit = (student, column) => {
    if (NON_EDITABLE_COLUMNS.includes(column)) return;
    closeAllModals();
    setEditingCell({ rno: student.Rno, column });
    const raw = student[column];
    if (isDateColumn(column) && raw) {
      const d = parseDateValue(raw);
      setEditValue(d ? d.toISOString().slice(0, 10) : '');
    } else if (typeof raw === 'boolean') {
      setEditValue(raw ? 'true' : 'false');
    } else {
      setEditValue(raw == null ? '' : String(raw));
    }
  };

  const cancelInlineEdit = () => {
    setEditingCell(null);
    setEditValue('');
    setSavingCell(false);
  };

  const saveInlineEdit = async () => {
    if (!editingCell || savingCell) return;
    const { rno, column } = editingCell;
    const original = students.find(s => s.Rno === rno)?.[column];

    // Coerce edit value to the original column's type
    let newValue = editValue;
    if (typeof original === 'boolean') {
      newValue = editValue === 'true';
    } else if (typeof original === 'number' && editValue !== '') {
      const n = Number(editValue);
      if (!Number.isNaN(n)) newValue = n;
    } else if (editValue === '') {
      newValue = null;
    }

    // No-op if unchanged
    if (String(original ?? '') === String(newValue ?? '')) {
      cancelInlineEdit();
      return;
    }

    setSavingCell(true);
    try {
      const { error } = await updateStudent(rno, { [column]: newValue });
      if (error) throw error;
      setStudents(prev => prev.map(s => s.Rno === rno ? { ...s, [column]: newValue } : s));
      setFilteredStudents(prev => prev.map(s => s.Rno === rno ? { ...s, [column]: newValue } : s));
      showNotification(`Updated ${formatColumnName(column)}`, 'success');
      cancelInlineEdit();
    } catch (err) {
      console.error('Inline edit failed:', err);
      showNotification(`Failed to update: ${err.message || 'Unknown error'}`, 'error');
      setSavingCell(false);
    }
  };


  // Show status confirmation modal
  const showStatusConfirmation = (rno) => {
    closeAllModals();
    
    const currentStudent = students.find(student => student.Rno === rno);
    if (!currentStudent) {
      console.error('Student not found with Rno:', rno);
      return;
    }
    
    const currentActive = currentStudent.Active || false;
    setCurrentStudentAction({
      ...currentStudent,
      action: currentActive ? 'deactivate' : 'activate',
      studentName: currentStudent.Name || 'this student'
    });
    setShowStatusModal(true);
  };

  // Handle status confirmation
  const handleStatusConfirm = async () => {
    if (!currentStudentAction) return;
    
    const { Rno, action } = currentStudentAction;
    const newActiveStatus = action === 'activate';
    
    try {
      setIsUpdatingStatus(true);
      
      // Update in Supabase
      const { error } = await updateStudent(Rno, {"Active": newActiveStatus});
      
      if (error) {
        throw error;
      }
      
      // Update UI
      setStudents(prevStudents => 
        prevStudents.map(student => 
          student.Rno === Rno 
            ? { ...student, Active: newActiveStatus } 
            : student
        )
      );
      
      setFilteredStudents(prev => 
        prev.map(student => 
          student.Rno === Rno 
            ? { ...student, Active: newActiveStatus } 
            : student
        )
      );
      
    } catch (error) {
      console.error('Error updating status:', error);
      showNotification(`Failed to ${action} student: ${error.message}`, 'error');
      fetchStudentsData();
    } finally {
      setIsUpdatingStatus(false);
      setShowStatusModal(false);
      setCurrentStudentAction(null);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showExportOptions && !e.target.closest('.export-container')) {
        setShowExportOptions(false);
      }
      if (showActiveDropdown && !e.target.closest('.active-filter-container')) {
        setShowActiveDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportOptions, showActiveDropdown]);
  
  // Handle export with options
  const handleExportClick = (activeOnly) => {
    setShowExportOptions(false);
    handleExport(activeOnly);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Parse dd/mm/yy or dd/mm/yyyy date strings
  const parseDateValue = (value) => {
    if (!value) return null;
    const str = String(value).trim();
    // Match dd/mm/yy or dd/mm/yyyy (also supports dd-mm-yy, dd-mm-yyyy)
    const match = str.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})$/);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      let year = parseInt(match[3], 10);
      if (year < 100) year += 2000; // 2-digit year: 25 → 2025
      return new Date(year, month, day);
    }
    // Fallback to native parsing
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  // Format cell value based on column type
  const formatCellValue = (value, columnName) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    
    // Format dates
    if (columnName.toLowerCase().includes('date') || columnName.toLowerCase() === 'dob') {
      const date = parseDateValue(value);
      if (date) {
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
      return value;
    }
    
    return String(value);
  };

  // Export to Excel
  const handleExport = async (activeOnly = false) => {
    try {
      let dataToExport = activeOnly 
        ? filteredStudents.filter(student => student.Active)
        : [...filteredStudents]; // Create a copy of the filtered array

      // Format data for export using dynamic columns
      const exportData = dataToExport.map(student => {
        const exportItem = {};
        columns.forEach(col => {
          exportItem[formatColumnName(col)] = student[col] || '';
        });
        // Add status if needed
        if (activeOnly) {
          exportItem['Status'] = 'Active';
        }
        return exportItem;
      });

      // Create a new workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      
      // Auto-size columns
      if (exportData.length > 0) {
        const columnWidths = Object.keys(exportData[0]).map(key => ({
          wch: Math.max(
            key.length, // header length
            ...exportData.map(row => String(row[key] || '').length) // max content length
          )
        }));
        ws['!cols'] = columnWidths;
      }
      
      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Students');
      
      // Generate the Excel file and trigger download
      const fileName = `students_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  
  // Fetch students from Supabase (used for refresh after save/edit)
  const fetchStudentsData = async (showLoader = false) => {
    if (showLoader) setIsLoading(true);
    setError(null);
    try {
      const studentsData = await fetchStudents();
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students. Please refresh the page.');
    } finally {
      if (showLoader) setIsLoading(false);
    }
  };
  
  useEffect(() => {
    const loadStudents = async () => {
      try {
        setIsLoading(true);
        const data = await fetchStudents();
        setStudents(data);
        setFilteredStudents(data);
        
        // Extract columns from the first student record
        if (data.length > 0) {
          const firstStudent = data[0];
          const excludedFields = ['id', 'created_at', 'updated_at'];
          const columnKeys = Object.keys(firstStudent).filter(
            key => !excludedFields.includes(key)
          );
          
          // Preferred column order — unlisted columns keep their relative position
          const PREFERRED_ORDER = [
            'Rno', 'Name', 'Gender', 'Class',
            'Pen No', 'Aadhar Name', 'DOB',
            'Mobile1', 'Mobile2', 'Aadhar No',
            'Religion', 'Category', 'Caste',
            "Father's Name (Son)", "Father's Name (Father)",
            "Mother's Name (Son)", "Mother's Name (Mother)",
            'Address Temporary', 'Address Permanent',
            'Date of Admission', 'Comment', 'Comment2',
            'Transport', 'Transport Fee', 'FeeDues', 'Default Discount',
          ];
          
          const ordered = [];
          const remaining = [];
          // Add columns in preferred order first
          PREFERRED_ORDER.forEach(col => {
            if (columnKeys.includes(col)) ordered.push(col);
          });
          // Append any remaining columns not in the preferred list
          columnKeys.forEach(col => {
            if (!PREFERRED_ORDER.includes(col)) remaining.push(col);
          });
          
          setColumns([...ordered, ...remaining]);
        }
      } catch (err) {
        console.error('Error loading students:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadStudents();
  }, []);

  // Auto-open edit mode if editRno query param is present (on initial load)
  useEffect(() => {
    if (editRnoHandled.current) return;
    const editRno = searchParams.get('editRno');
    if (editRno && students.length > 0) {
      editRnoHandled.current = true;
      if (editRno === '-1') {
        setEditingStudent(null);
        setShowStudentForm(true);
      } else {
        const student = students.find(s => String(s.Rno) === editRno);
        if (student) {
          setEditingStudent(student);
          setShowStudentForm(true);
        }
      }
    }
  }, [students, searchParams]);

  // Filter students when search term, filters, or active filter changes
  useEffect(() => {
    const filtered = students.filter(student => {
      // Active filter
      if (activeFilter === 'active' && student.Active === false) return false;
      if (activeFilter === 'inactive' && student.Active !== false) return false;

      // Multi-filter: group consecutive OR conditions, then AND the groups
      if (filters.length > 0) {
        const groups = [];
        let currentGroup = [filters[0]];
        for (let i = 1; i < filters.length; i++) {
          if (filters[i].connector === 'OR') {
            currentGroup.push(filters[i]);
          } else {
            groups.push(currentGroup);
            currentGroup = [filters[i]];
          }
        }
        groups.push(currentGroup);
        const passes = groups.every(group => group.some(f => applyFilter(student, f)));
        if (!passes) return false;
      }

      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return Object.entries(student).some(([key, val]) =>
        !['id', 'created_at', 'updated_at', 'Active'].includes(key) &&
        String(val).toLowerCase().includes(searchLower)
      );
    });
    setFilteredStudents(filtered);
  }, [searchTerm, students, filters, activeFilter]);

  // Reset to first page only when search/filter criteria change (not on data reload)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters, activeFilter]);

  // Sort the filtered students
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    // Handle null or undefined values
    if (!a[sortField]) return sortDirection === 'asc' ? -1 : 1;
    if (!b[sortField]) return sortDirection === 'asc' ? 1 : -1;
    
    // Sort strings case-insensitive
    if (typeof a[sortField] === 'string' && typeof b[sortField] === 'string') {
      const comparison = a[sortField].localeCompare(b[sortField], undefined, { sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    }
    
    // Sort numbers
    if (typeof a[sortField] === 'number' && typeof b[sortField] === 'number') {
      return sortDirection === 'asc' ? a[sortField] - b[sortField] : b[sortField] - a[sortField];
    }
    
    return 0;
  });
  
  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedStudents.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedStudents.length / recordsPerPage);

  // Clamp currentPage if it exceeds totalPages after data reload
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);
  
  // Reset to first page when sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortField, sortDirection]);
  
  // Keyboard navigation for cell selection
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedCell || editingCell) return;
      
      const { rno, column } = selectedCell;
      const rowIndex = currentRecords.findIndex(s => s.Rno === rno);
      const colIndex = columns.indexOf(column);
      if (rowIndex === -1 || colIndex === -1) return;

      let newRowIndex = rowIndex;
      let newColIndex = colIndex;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newRowIndex = Math.max(0, rowIndex - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newRowIndex = Math.min(currentRecords.length - 1, rowIndex + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newColIndex = Math.max(0, colIndex - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newColIndex = Math.min(columns.length - 1, colIndex + 1);
          break;
        case 'Enter':
          e.preventDefault();
          if (!NON_EDITABLE_COLUMNS.includes(column)) {
            startInlineEdit(currentRecords[rowIndex], column);
          }
          return;
        case 'Escape':
          e.preventDefault();
          setSelectedCell(null);
          return;
        default:
          return;
      }

      const newRno = currentRecords[newRowIndex].Rno;
      const newCol = columns[newColIndex];
      setSelectedCell({ rno: newRno, column: newCol });

      // Scroll the cell into view
      const cell = tableRef.current?.querySelector(
        `td[data-col="${newCol}"][class*=""]`
      );
      const targetRow = tableRef.current?.querySelectorAll('tbody tr')[newRowIndex];
      const targetCell = targetRow?.querySelector(`td[data-col="${newCol}"]`);
      if (targetCell) {
        targetCell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, editingCell, currentRecords, columns]);

  // Function to change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // Render pagination controls
  const renderPagination = () => {
    // Always show pagination even with one page so it shows the record count
    
    const pageNumbers = [];
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="pagination">
        {totalPages > 1 && (
          <>
            <button 
              onClick={() => paginate(1)} 
              disabled={currentPage === 1}
              className="page-nav"
              aria-label="First page"
            >
              &laquo;
            </button>
            <button 
              onClick={() => paginate(currentPage - 1)} 
              disabled={currentPage === 1}
              className="page-nav"
              aria-label="Previous page"
            >
              &lsaquo;
            </button>
            
            {startPage > 1 && (
              <span className="page-ellipsis">...</span>
            )}
            
            {pageNumbers.map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`page-number ${currentPage === number ? 'active' : ''}`}
                aria-label={`Page ${number}`}
              >
                {number}
              </button>
            ))}
            
            {endPage < totalPages && (
              <span className="page-ellipsis">...</span>
            )}
            
            <button 
              onClick={() => paginate(currentPage + 1)} 
              disabled={currentPage === totalPages}
              className="page-nav"
              aria-label="Next page"
            >
              &rsaquo;
            </button>
            <button 
              onClick={() => paginate(totalPages)} 
              disabled={currentPage === totalPages}
              className="page-nav"
              aria-label="Last page"
            >
              &raquo;
            </button>
          </>
        )}
        
        <div className="pagination-info">
          {sortedStudents.length > 0 ? (
            <>Showing {indexOfFirstRecord + 1} - {Math.min(indexOfLastRecord, sortedStudents.length)} of {sortedStudents.length} records</>
          ) : (
            <>No records to display</>
          )}
        </div>
      </div>
    );
  };

  // Close all modals
  const closeAllModals = () => {
    setShowStatusModal(false);
    setShowStudentForm(false);
    setShowInlineStatusConfirm(false);
    setCurrentStudentAction(null);
    setEditingStudent(null);
    // Clear editRno from URL
    if (searchParams.has('editRno')) {
      searchParams.delete('editRno');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Handle edit action
  const handleEdit = (student) => {
    closeAllModals();
    setEditingStudent(student);
    setShowStudentForm(true);
    setSearchParams({ editRno: String(student.Rno) }, { replace: true });
  };

  // Handle add student
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, show: false }));
  };

  const handleAddStudent = async (newStudent) => {
    setIsSaving(true);
    try {
      await addStudent(newStudent);
      closeAllModals();
      showNotification('Student added successfully!', 'success');
      // Refetch from DB to confirm save, page stays as-is
      await fetchStudentsData();
    } catch (error) {
      console.error('Error adding student:', error);
      showNotification('Failed to add student. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle update student
  const handleUpdateStudent = async (updatedData) => {
    if (!editingStudent) return;
    
    setIsSaving(true);
    try {
      const { error } = await updateStudent(editingStudent.Rno, updatedData);
      
      if (error) throw error;
      
      closeAllModals();
      showNotification('Student updated successfully!', 'success');
      // Refetch from DB to confirm save, page stays as-is
      await fetchStudentsData();
    } catch (error) {
      console.error('Error updating student:', error);
      showNotification(`Failed to update student: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInlineActivateToggle = (formData) => {
    setShowInlineStatusConfirm(true);
  };

  const handleInlineStatusConfirm = async () => {
    if (!editingStudent) return;
    const newActive = !editingStudent.Active;
    try {
      setIsUpdatingStatus(true);
      const { error } = await updateStudent(editingStudent.Rno, { Active: newActive });
      if (error) throw error;
      const updatedStudent = { ...editingStudent, Active: newActive };
      setEditingStudent(updatedStudent);
      setStudents(prev => prev.map(s => s.Rno === editingStudent.Rno ? { ...s, Active: newActive } : s));
      setFilteredStudents(prev => prev.map(s => s.Rno === editingStudent.Rno ? { ...s, Active: newActive } : s));
      showNotification(`Student ${newActive ? 'activated' : 'deactivated'} successfully!`, 'success');
    } catch (err) {
      console.error('Error updating status:', err);
      showNotification(`Failed to update status: ${err.message}`, 'error');
    } finally {
      setIsUpdatingStatus(false);
      setShowInlineStatusConfirm(false);
    }
  };

  const handlePayFromEdit = (formData) => {
    navigate(`/academic/fees?payRno=${editingStudent.Rno}`);
  };

  // Calculate student summary
  const getStudentSummary = () => {
    if (!students || students.length === 0) return { total: 0, active: 0 };
    
    const activeCount = students.filter(student => student.Active).length;
    return {
      total: students.length,
      active: activeCount,
      inactive: students.length - activeCount
    };
  };
  
  const studentSummary = getStudentSummary();

  return (
    <div className="students-container">
      <header className="students-header">
        <div className="header-left">
          <h1>Students Management</h1>
        </div>
        <div className="student-summary">
          <div className="student-summary-item">
            <span className="summary-label">Total Students:</span>
            <span className="summary-value">{studentSummary.total}</span>
          </div>
          <div className="student-summary-item">
            <span className="summary-label">Active:</span>
            <span className="summary-value active">{studentSummary.active}</span>
          </div>
          <div className="student-summary-item">
            <span className="summary-label">Inactive:</span>
            <span className="summary-value inactive">{studentSummary.inactive}</span>
          </div>
        </div>
      </header>

      <div className="students-controls">
        <div className="search-container">
          <div className="student-search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search all columns..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>

          {/* Filter Builder Toggle */}
          <button
            className={`filter-builder-toggle ${filters.length > 0 ? 'active' : ''}`}
            onClick={() => filters.length === 0 ? addFilter() : setShowFilterBuilder(!showFilterBuilder)}
          >
            <i className="fas fa-filter"></i>
            <span>Filters</span>
            {filters.length > 0 && <span className="filter-count">{filters.length}</span>}
          </button>

          {/* Active Students Filter */}
          <div className="active-filter-container" style={{ position: 'relative' }}>
            <button
              className={`active-filter-btn ${activeFilter !== 'active' ? 'filtered' : ''}`}
              onClick={() => setShowActiveDropdown(!showActiveDropdown)}
            >
              <i className={`fas ${activeFilter === 'active' ? 'fa-toggle-on' : activeFilter === 'inactive' ? 'fa-toggle-off' : 'fa-users'}`}></i>
              <span>{activeFilter === 'active' ? 'Active' : activeFilter === 'inactive' ? 'Inactive' : 'All'}</span>
              <i className={`fas fa-chevron-${showActiveDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showActiveDropdown && (
              <div className="active-filter-dropdown">
                {[
                  { value: 'active', label: 'Active', icon: 'fa-toggle-on' },
                  { value: 'inactive', label: 'Inactive', icon: 'fa-toggle-off' },
                  { value: 'all', label: 'All', icon: 'fa-users' },
                ].map(opt => (
                  <div
                    key={opt.value}
                    className={`active-filter-option ${activeFilter === opt.value ? 'selected' : ''}`}
                    onClick={() => { setActiveFilter(opt.value); setShowActiveDropdown(false); }}
                  >
                    <i className={`fas ${opt.icon}`}></i>
                    <span>{opt.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="button-group">
          <div className="export-container" style={{ position: 'relative' }}>
            <button 
              className="export-btn" 
              onClick={() => setShowExportOptions(!showExportOptions)}
            >
              <i className="fas fa-file-export"></i> Export to Excel
            </button>
            {showExportOptions && (
              <div className="export-options" style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '200px'
              }}>
                <div 
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  onClick={() => handleExportClick(true)}
                >
                  Export Active Students Only
                </div>
                <div 
                  style={{
                    padding: '10px 15px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #eee',
                    backgroundColor: '#fff',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                  onClick={() => handleExportClick(false)}
                >
                  Export All Students
                </div>
              </div>
            )}
          </div>
          <button 
            className="add-student-btn"
            onClick={() => {
              closeAllModals();
              setEditingStudent(null);
              setShowStudentForm(true);
              setSearchParams({ editRno: '-1' }, { replace: true });
            }}
          >
            <i className="fas fa-plus"></i> Add New Student
          </button>
        </div>
      </div>

      {/* Filter Builder Panel */}
      {showFilterBuilder && filters.length > 0 && (
        <div className="filter-builder-panel">
          {filters.map((filter, index) => (
            <div key={index} className="filter-row">
              {index > 0 && (
                <button
                  className={`filter-connector ${filter.connector === 'OR' ? 'or' : ''}`}
                  onClick={() => updateFilter(index, 'connector', filter.connector === 'AND' ? 'OR' : 'AND')}
                  title="Click to toggle AND/OR"
                >{filter.connector}</button>
              )}
              <select
                className="filter-select filter-column"
                value={filter.column}
                onChange={e => updateFilter(index, 'column', e.target.value)}
              >
                {columns.filter(col => col !== 'Active').map(col => (
                  <option key={col} value={col}>{formatColumnName(col)}</option>
                ))}
              </select>
              <select
                className="filter-select filter-operator"
                value={filter.operator}
                onChange={e => updateFilter(index, 'operator', e.target.value)}
              >
                {FILTER_OPERATORS.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
              {!['is_empty', 'is_not_empty'].includes(filter.operator) && (
                <input
                  type="text"
                  className="filter-value-input"
                  placeholder="Value..."
                  value={filter.value}
                  onChange={e => updateFilter(index, 'value', e.target.value)}
                />
              )}
              <button className="filter-remove-btn" onClick={() => removeFilter(index)} title="Remove filter">
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
          <div className="filter-actions">
            <button className="filter-add-btn" onClick={() => addFilter('AND')}>
              <i className="fas fa-plus"></i> AND
            </button>
            <button className="filter-add-btn filter-add-or" onClick={() => addFilter('OR')}>
              <i className="fas fa-plus"></i> OR
            </button>
            <button className="filter-clear-btn" onClick={() => { clearAllFilters(); setShowFilterBuilder(false); }}>
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Loading and error states */}
      {isLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading students data...</p>
        </div>
      )}
      
      {error && (
        <div className="error-container">
          <p><i className="fas fa-exclamation-circle"></i> {error}</p>
          <button onClick={fetchStudentsData} className="retry-btn">
            <i className="fas fa-sync"></i> Retry
          </button>
        </div>
      )}

      <div className="students-table-container">
        <div className="table-scroll-wrapper">
          <table className="students-table" ref={tableRef}>
          <colgroup>
            {/* Actions column — 120px (matches sticky offset for Rno) */}
            <col style={{ width: '120px', minWidth: '120px' }} />
            {columns.map((col) => {
              // Rno is narrow (120px) to match Actions; everything else is 200px.
              const w = col === 'Rno' ? '120px' : '200px';
              return (
                <col
                  key={`col-${col}`}
                  style={{ width: w, minWidth: w }}
                />
              );
            })}
          </colgroup>
          <thead>
            <tr>
              <th className="actions-header">
                <div className="header-content">
                  <span className="header-text">Actions</span>
                  <button 
                    className={`sort-btn ${sortField === 'Active' ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSort('Active');
                    }}
                  >
                    <i className={`fas fa-sort${sortField === 'Active' ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                  </button>
                </div>
              </th>
              {columns.map((column) => {
                const stickyClass =
                  column === 'Rno' ? 'sticky-col sticky-col-rno' :
                  column === 'Name' ? 'sticky-col sticky-col-name' : '';
                return (
                  <th key={column} data-col={column} className={stickyClass}>
                    <div className="header-content">
                      <span className="header-text">{formatColumnName(column)}</span>
                      <button
                        className={`sort-btn ${sortField === column ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort(column);
                        }}
                      >
                        <i className={`fas fa-sort${sortField === column ? (sortDirection === 'asc' ? '-up' : '-down') : ''}`}></i>
                      </button>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {currentRecords.length > 0 ? (
              currentRecords.map((student) => (
                <tr
                  key={student.Rno}
                  className={!student.Active ? 'inactive-student' : ''}
                >
                  <td className="actions-cell">
                    <div className="action-buttons">
                      <button 
                        className={`status-toggle ${student.Active ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          showStatusConfirmation(student.Rno);
                        }}
                        title={student.Active ? 'Deactivate' : 'Activate'}
                        disabled={isUpdatingStatus}
                      >
                        {isUpdatingStatus && currentStudentAction?.Rno === student.Rno ? (
                          <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                          <i className={`fas ${student.Active ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                        )}
                      </button>
                      <button 
                        className="edit-btn" 
                        title="Edit (open full form)"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(student);
                        }}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                    </div>
                  </td>
                  {columns.map((col) => {
                    const isEditing = editingCell?.rno === student.Rno && editingCell?.column === col;
                    const isEditable = !NON_EDITABLE_COLUMNS.includes(col);
                    const stickyClass =
                      col === 'Rno' ? 'sticky-col sticky-col-rno' :
                      col === 'Name' ? 'sticky-col sticky-col-name' : '';
                    const original = student[col];
                    const isBoolean = typeof original === 'boolean';
                    const inputType = isDateColumn(col) ? 'date' : 'text';

                    return (
                      <td
                        key={`${student.Rno}-${col}`}
                        data-col={col}
                        className={`${stickyClass} ${isEditable ? 'editable-cell' : ''} ${isEditing ? 'editing' : ''} ${selectedCell?.rno === student.Rno && selectedCell?.column === col ? 'selected-cell' : ''}`}
                        title={isEditing ? '' : `Click to select · Double-click to edit · ${String(original ?? '')}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCell({ rno: student.Rno, column: col });
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (isEditable) startInlineEdit(student, col);
                        }}
                      >
                        {isEditing ? (
                          isBoolean ? (
                            <select
                              className="inline-edit-input"
                              value={editValue}
                              autoFocus
                              disabled={savingCell}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveInlineEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                if (e.key === 'Escape') { e.preventDefault(); cancelInlineEdit(); }
                              }}
                            >
                              <option value="true">True</option>
                              <option value="false">False</option>
                            </select>
                          ) : (
                            <input
                              type={inputType}
                              className="inline-edit-input"
                              value={editValue}
                              autoFocus
                              disabled={savingCell}
                              onChange={(e) => setEditValue(e.target.value)}
                              onBlur={saveInlineEdit}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); saveInlineEdit(); }
                                if (e.key === 'Escape') { e.preventDefault(); cancelInlineEdit(); }
                              }}
                            />
                          )
                        ) : (
                          <span className="cell-content">
                            {formatCellValue(original, col)}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length + 1} className="no-records">
                  <div className="no-records-content">
                    <i className="fas fa-search" style={{ fontSize: '1.5rem' }}></i>
                    <p>No students found matching your search criteria.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
          </table>
        </div>
      </div>
      
      {/* Pagination */}
      {renderPagination()}
      
      {/* Notification */}
      {notification.show && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={closeNotification}
        />
      )}

      {/* Status Confirmation Modal */}
      {showStatusModal && currentStudentAction && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Student Details - {currentStudentAction.studentName || 'N/A'}</h3>
              <button className="student-close-btn" onClick={closeAllModals}>&times;</button>
            </div>
            
            <div className="student-details-grid">
              <div className="detail-item">
                <span className="detail-label">Roll No:</span>
                <span className="detail-value">{currentStudentAction.Rno || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Class:</span>
                <span className="detail-value">{currentStudentAction.Class || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Father's Name:</span>
                <span className="detail-value">{currentStudentAction["Father's Name (Son)"] || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Mother's Name:</span>
                <span className="detail-value">{currentStudentAction["Mother's Name (Son)"] || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone 1:</span>
                <span className="detail-value">{currentStudentAction.Mobile1 || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Status:</span>
                <span className={`status-badge ${currentStudentAction.Active ? 'active' : 'inactive'}`}>
                  {currentStudentAction.Active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="confirmation-message">
              Are you sure you want to {currentStudentAction.action} 
              <strong> {currentStudentAction.studentName}</strong>?
              <div className="action-note">
                {currentStudentAction.action === 'deactivate' 
                  ? 'This will prevent the student from accessing the system.'
                  : 'This will allow the student to access the system.'}
              </div>
            </div>

            <div className="modal-actions">
              <button 
                type="button"
                className="btn btn-secondary" 
                onClick={closeAllModals}
                disabled={isUpdatingStatus}
              >
                Cancel
              </button>
              <button 
                type="button"
                className={`btn ${currentStudentAction.action === 'activate' ? 'btn-success' : 'btn-warning'}`}
                onClick={handleStatusConfirm}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i> Updating...
                  </>
                ) : (
                  `Yes, ${currentStudentAction.action}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Form Modal */}
      {showStudentForm && (
        <div className="modal-overlay" onClick={closeAllModals}>
          <div className="modal-content student-form-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingStudent ? 'Edit Student' : 'Add New Student'}</h3>
              <button className="student-close-btn" onClick={closeAllModals}>&times;</button>
            </div>
            <div className="modal-body">
              <StudentForm
                initialData={editingStudent || {}}
                onSave={editingStudent ? handleUpdateStudent : handleAddStudent}
                isSaving={isSaving}
                onClose={closeAllModals}
                isEditMode={!!editingStudent}
                columns={columns}
                onActivateToggle={editingStudent ? handleInlineActivateToggle : undefined}
                onPay={editingStudent ? handlePayFromEdit : undefined}
              />
              {showInlineStatusConfirm && editingStudent && (
                <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => setShowInlineStatusConfirm(false)}>
                  <div className="modal-content" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3>{editingStudent.Active ? 'Deactivate' : 'Activate'} Student</h3>
                      <button className="student-close-btn" onClick={() => setShowInlineStatusConfirm(false)}>&times;</button>
                    </div>
                    <div className="confirmation-message">
                      Are you sure you want to {editingStudent.Active ? 'deactivate' : 'activate'}
                      <strong> {editingStudent.Name || 'this student'}</strong>?
                      <div className="action-note">
                        {editingStudent.Active
                          ? 'This will prevent the student from accessing the system.'
                          : 'This will allow the student to access the system.'}
                      </div>
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowInlineStatusConfirm(false)} disabled={isUpdatingStatus}>Cancel</button>
                      <button type="button" className={`btn ${editingStudent.Active ? 'btn-warning' : 'btn-success'}`} onClick={handleInlineStatusConfirm} disabled={isUpdatingStatus}>
                        {isUpdatingStatus ? (<><i className="fas fa-spinner fa-spin"></i> Updating...</>) : `Yes, ${editingStudent.Active ? 'deactivate' : 'activate'}`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Students;
